/**
 * Classify files for the unified quote Excel / order-confirmation CSV upload control.
 */

/**
 * @param {string} fileName
 * @returns {boolean}
 */
function isCsvFile(fileName) {
  return /\.csv$/i.test(String(fileName || ''));
}

const MAX_EXCEL_BYTES = 10 * 1024 * 1024;
const MAX_CSV_BYTES = 5 * 1024 * 1024;

const EXCEL_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];

/**
 * @param {File} file
 * @returns {boolean}
 */
export function isValidExcelQuoteFile(file) {
  const validExtensions = ['.xlsx', '.xls'];
  return (
    EXCEL_MIME_TYPES.includes(file.type) ||
    validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
  );
}

/**
 * @param {File} file
 * @returns {'excel'|'csv'|'unknown'}
 */
export function getQuoteSpreadsheetUploadFileKind(file) {
  if (isCsvFile(file.name)) return 'csv';
  if (isValidExcelQuoteFile(file)) return 'excel';
  return 'unknown';
}

/**
 * Validate and partition selected files for upload routing.
 *
 * @param {File[]} files
 * @returns {{
 *   mode: 'excel'|'order_confirmation'|null,
 *   excelFiles: File[],
 *   csvFile: File|null,
 *   errors: string[]
 * }}
 */
export function classifyQuoteSpreadsheetUploadFiles(files) {
  /** @type {string[]} */
  const errors = [];
  /** @type {File[]} */
  const excelFiles = [];
  /** @type {File[]} */
  const csvFiles = [];

  for (const file of files) {
    const kind = getQuoteSpreadsheetUploadFileKind(file);
    if (kind === 'unknown') {
      errors.push(
        `${file.name}: Not supported. Use Excel (.xlsx, .xls) or Sigma order confirmation CSV (.csv).`
      );
      continue;
    }
    if (kind === 'csv') {
      if (file.size > MAX_CSV_BYTES) {
        errors.push(`${file.name}: CSV must be less than 5MB`);
        continue;
      }
      csvFiles.push(file);
      continue;
    }
    if (file.size > MAX_EXCEL_BYTES) {
      errors.push(`${file.name}: Excel file must be less than 10MB`);
      continue;
    }
    excelFiles.push(file);
  }

  if (excelFiles.length > 0 && csvFiles.length > 0) {
    errors.push(
      'Cannot mix Excel requisition files with order confirmation CSV in one selection. Select one type at a time.'
    );
  }
  if (csvFiles.length > 1) {
    errors.push('Select only one order confirmation CSV at a time.');
  }

  if (errors.length > 0) {
    return { mode: null, excelFiles: [], csvFile: null, errors };
  }

  if (csvFiles.length === 1) {
    return {
      mode: 'order_confirmation',
      excelFiles: [],
      csvFile: csvFiles[0],
      errors: [],
    };
  }

  if (excelFiles.length > 0) {
    return { mode: 'excel', excelFiles, csvFile: null, errors: [] };
  }

  return { mode: null, excelFiles: [], csvFile: null, errors: [] };
}
